/**
 * components/shared/RdvRequestModal.js
 *
 * Modal "Demander un RDV" partagé IDE + MEDECIN.
 * Appelle POST /appointments/requests → la SEC valide et programme.
 *
 * Factorisé depuis le modal historique inline de NursePatientDetail.
 */
import { useState } from "react";
import { Calendar, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

function formatApiErrorDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || String(item)).join(" ") || fallback;
  }
  if (typeof detail === "object") return detail.message || detail.msg || fallback;
  return String(detail);
}

export default function RdvRequestModal({ patientId, onClose, onSubmitted }) {
  const [type, setType] = useState("consultation");
  const [urgency, setUrgency] = useState("normal");
  const [preferredDate, setPreferredDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/appointments/requests", {
        patientId,
        type,
        urgency,
        notes: notes || null,
        preferredDate: preferredDate || null,
      });
      toast.success("Demande de RDV envoyée");
      onSubmitted && onSubmitted();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Erreur lors de l'envoi"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="rdv-request-modal"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">Demander un RDV</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg"
            aria-label="Fermer"
            data-testid="close-rdv-request-modal-btn"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-600 block mb-2">Type</label>
          <div className="flex gap-2">
            <button
              onClick={() => setType("consultation")}
              data-testid="rdv-type-consultation"
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                type === "consultation"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 text-slate-500"
              }`}
            >
              Consultation
            </button>
            <button
              onClick={() => setType("imagerie")}
              data-testid="rdv-type-imagerie"
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                type === "imagerie"
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 text-slate-500"
              }`}
            >
              Imagerie
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-600 block mb-2">Urgence</label>
          <div className="flex gap-2">
            <button
              onClick={() => setUrgency("normal")}
              data-testid="rdv-urgency-normal"
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                urgency === "normal"
                  ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                  : "border-slate-200 text-slate-500"
              }`}
            >
              Normal
            </button>
            <button
              onClick={() => setUrgency("urgent")}
              data-testid="rdv-urgency-urgent"
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                urgency === "urgent"
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-slate-200 text-slate-500"
              }`}
            >
              Urgent
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-600 block mb-1">
            Date souhaitée (indicatif)
          </label>
          <input
            type="date"
            value={preferredDate}
            onChange={(e) => setPreferredDate(e.target.value)}
            data-testid="rdv-preferred-date"
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-slate-600 block mb-1">Motif / Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Précisez le motif..."
            rows={2}
            data-testid="rdv-notes"
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            data-testid="cancel-rdv-request-btn"
            className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={loading}
            data-testid="submit-rdv-request-btn"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            {loading ? "Envoi..." : "Envoyer la demande"}
          </button>
        </div>
      </div>
    </div>
  );
}
